import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Text, Button, Drawer, ScrollArea } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import Swal from "sweetalert2";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';

import BundlesForm from "./BundlesForm";
import PremiumModal from "../../../app/PremiumModal";
import BundleSortableItem from "../../../ui/molecules/BundleSortableItem";

import BundleModel from "../../../../models/Bundle";
import StripeModel from "../../../../models/Stripe";

import { colors } from "../../../../collections/colors";
import { sutraTheme } from "../../../../collections/sutraTheme";
import { reorderOptions } from "../../../../utils/Filters";

export default function Bundles(props) {
  const { t } = useTranslation();

  const [opened, setOpened] = useState(false);
  const [bundle, setBundle] = useState({});
  const [bundles, setBundles] = useState([]);
  const [validateOpened, setValidateOpened] = useState(false);
  const [validatePremium, setValidatePremium] = useState(false);

  const space = useSelector((state) => state.space);

  useEffect(() => {
    onFetch();
  }, [props]);

  useEffect(() => {
    setValidatePremium(
      space && space.permissions && space.permissions.can_create_bundles
    );
  }, [space]);

  const onFetch = () => {
    BundleModel.onFetch(
      space.slug,
      (data) => {
        if (Array.isArray(data)) {
          setBundles(data.sort());
        }
      },
      (data) => {}
    );
  };

  const onCreate = () => {
    setBundle({});
    setOpened(true);
  };

  const onUpdate = (id) => {
    onFetch();
    setOpened(false);
  };

  const onEdit = (item) => {
    setBundle(item);
    setOpened(true);
  };

  const onDestroy = (id) => {
    Swal.fire({
      title: "Are you sure?",
      html: "This bundle will be inactive for future uses.",
      showDenyButton: true,
      confirmButtonText: "Yes, delete",
      denyButtonText: "No",
      confirmButtonColor: colors.red,
      denyButtonColor: colors.gray,
    }).then((result) => {
      if (result.isConfirmed) {
        BundleModel.onDestroy(
          space.id,
          id,
          (data) => onFetch(),
          (err) => {}
        );
      }
    });
  };

  const onReorder = (updatedItems) => {
    const order = updatedItems.map((item) => ({
      id: item.id,
      sort_order: item.sort_order
    }));
    if (!Array.isArray(order)) return;
    BundleModel.onReorder(
      space.slug,
      order,
      (data) => onFetch(),
      (err) => {}
    );
  };

  const onConnect = () => {
    StripeModel.onConnect(
      space.slug,
      (data) => {
        if (data.url) {
          window.location.href= data.url;
        }
      },
      (data) => {}
    );
  };

  return (
    <>
      {space?.can_accept_charges ? (
        <Button
          mb={20}
          variant="filled"
          radius="xl"
          onClick={() => {
            if (validatePremium && !validatePremium[0]) {
              setValidateOpened([true, validatePremium[1], validatePremium[2]]);
            } else {
              onCreate();
            }
          }}
          leftIcon={<FontAwesomeIcon icon={solid("cubes")} />}
          sx={{
            backgroundColor: sutraTheme.colors.sutrablue[3],
            "&:hover": {
              backgroundColor: sutraTheme.colors.sutrablue[4],
            },
          }}
        >
          {"Create bundle"}
        </Button>
      ) : (
        <Button radius="xl" onClick={() => onConnect()}>
          {t("spaces.settings.privacy.paid.paid.connectButton")}
        </Button>
      )}
      
      <DndContext
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={({ active, over }) => {
          if (active.id !== over?.id) {
            const oldIndex = bundles.findIndex((i) => i.id === active.id);
            const newIndex = bundles.findIndex((i) => i.id === over?.id);
            const updatedItems = reorderOptions(bundles, oldIndex, newIndex);
            setBundles(updatedItems);
            onReorder(updatedItems);
          }
        }}
        collisionDetection={closestCenter}
      >
        <SortableContext items={bundles.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          {bundles.map((item) => {
            return (
              <BundleSortableItem
                key={item.id}
                id={item.id}
                name={item.name}
                currency={item.currency}
                default_payment_amount={item.default_payment_amount}
                default_payment_frequency={item.default_payment_frequency}
                space_slug={space.slug}
                onEdit={() => onEdit(item)}
                onDestroy={() => onDestroy(item.id)}
              />
            );
          })}
        </SortableContext>
      </DndContext>

      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        title={
          <Text size="xl" weight="800">
            {bundle?.id ? "Edit bundle" : "New bundle"}
          </Text>
        }
        size={600}
        padding={0}
        classNames={{ header: { padding: "20px", paddingBottom: 0 } }}
      >
        <ScrollArea style={{ height: "100%", padding: "20px", paddingTop: 0 }}>
          <BundlesForm
            opened={opened}
            setOpened={setOpened}
            bundle={bundle}
            spaceId={space.id}
            onUpdate={onUpdate}
          />
        </ScrollArea>
      </Drawer>
      <PremiumModal opened={validateOpened} setOpened={setValidateOpened} />
    </>
  );
}
